/**
 * BoltConversationSidebar — list of persisted BOLT conversations.
 * Used in BoltTab (collapsible column) and BoltFab (full-panel drawer).
 */

import React from 'react';
import { Plus, Loader2, ChevronLeft, MessageSquare } from 'lucide-react';
import { useBoltConversations } from '../../../hooks/useBoltData';
import BoltConversationItem from './BoltConversationItem';

export default function BoltConversationSidebar({
  jobsiteId,
  activeConversationId,
  onSelect,
  onNew,
  isOpen,
  onToggle,
}) {
  const { conversations, loading, error, deleteConversation, renameConversation } =
    useBoltConversations(jobsiteId);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await deleteConversation(id);
      if (id === activeConversationId) {
        onNew?.();
      }
    } catch {
      // Hook already logs; optional toast could go here
    }
  };

  const handleRename = async (id, currentTitle) => {
    const next = window.prompt('Conversation title', currentTitle || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    try {
      await renameConversation(id, trimmed);
    } catch {
      /* logged in client */
    }
  };

  return (
    <aside
      className={`bolt-conversation-sidebar ${!isOpen ? 'collapsed' : ''}`}
      aria-hidden={!isOpen}
    >
      <div className="bolt-conversation-sidebar__header">
        {onToggle && (
          <button
            type="button"
            className="bolt-conversation-sidebar__collapse"
            onClick={onToggle}
            aria-label="Collapse conversation list"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        <span className="bolt-conversation-sidebar__label">Conversations</span>
        <button
          type="button"
          className="bolt-conversation-sidebar__new"
          onClick={() => onNew?.()}
          aria-label="New conversation"
        >
          <Plus size={18} />
        </button>
      </div>

      <div className="bolt-conversation-sidebar__body">
        {loading && (
          <div className="bolt-conversation-sidebar__loading">
            <Loader2 size={20} className="bolt-conversation-sidebar__spinner" />
            <span>Loading…</span>
          </div>
        )}
        {!loading && error && (
          <div className="bolt-conversation-sidebar__empty">
            Could not load conversations.
          </div>
        )}
        {!loading && !error && conversations.length === 0 && (
          <div className="bolt-conversation-sidebar__empty">
            <MessageSquare size={24} aria-hidden />
            <p>No conversations yet.</p>
            <p className="bolt-conversation-sidebar__empty-hint">Send a message to start one.</p>
          </div>
        )}
        {!loading &&
          !error &&
          conversations.map((c) => (
            <BoltConversationItem
              key={c.conversationId || c._id}
              conversation={c}
              isActive={(c.conversationId || c._id) === activeConversationId}
              onSelect={onSelect}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}
      </div>
    </aside>
  );
}
